import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { BASE_URL } from "../config";
import { COUNTRY_NAME_TO_CODE, COUNTRY_CODE_TO_NAME } from "../utils/countries";
import GooglePlacesInput from "./GoogleAutocompleteInput";
import { showError } from "../utils/dialog";



const normalizeCountryToISO2 = (val) => {
  if (!val) return null;

  // Already ISO-2
  if (val.length === 2) return val.toUpperCase();

  // Exact map (United States -> US)
  if (COUNTRY_NAME_TO_CODE[val]) {
    return COUNTRY_NAME_TO_CODE[val];
  }

  // Handle common Google variants
  if (val.toLowerCase().includes("usa")) return "US";
  if (val.toLowerCase().includes("united states")) return "US";
  if (val.toLowerCase().includes("uk")) return "GB";
  if (val.toLowerCase().includes("united kingdom")) return "GB";

  // Handle common country names
  if (val.toLowerCase().includes("canada")) return "CA";
  if (val.toLowerCase().includes("canada")) return "CA";

  return null;
};

const extractCountryISO2FromRegionValue = (val) => {
  if (!val || typeof val !== "string") return null;
  const parts = val
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  if (parts.length < 2) return null;
  const maybeCountry = parts[parts.length - 1];
  return normalizeCountryToISO2(maybeCountry);
};

const REGION_CODE_TO_NAME = {
  US: {
    AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
    CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
    HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
    KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
    MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
    MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
    NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
    OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
    SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
    VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
    DC: "District of Columbia",
  },
  CA: {
    AB: "Alberta", BC: "British Columbia", MB: "Manitoba", NB: "New Brunswick",
    NL: "Newfoundland and Labrador", NS: "Nova Scotia", NT: "Northwest Territories",
    NU: "Nunavut", ON: "Ontario", PE: "Prince Edward Island", QC: "Quebec",
    SK: "Saskatchewan", YT: "Yukon",
  },
};

const REGION_NAME_TO_CODE = (() => {
  const map = {};
  Object.entries(REGION_CODE_TO_NAME).forEach(([, codeMap]) => {
    Object.entries(codeMap).forEach(([code, name]) => {
      map[String(name).toLowerCase()] = code;
      map[String(code).toLowerCase()] = code;
    });
  });
  return map;
})();

const toRegionDisplayName = (region, countryCode) => {
  const r = String(region || "").trim();
  if (!r) return "";
  if (r.length !== 2) return r;
  const cc = String(countryCode || "").trim().toUpperCase();
  const mapped = REGION_CODE_TO_NAME[cc]?.[r.toUpperCase()];
  return mapped || r;
};

const toRegionCode = (val) => {
  if (!val || typeof val !== "string") return "";
  const trimmed = val.trim();
  if (!trimmed) return "";

  // Handles values like "Florida, United States" from places/autocomplete.
  const regionPart = trimmed.split(",")[0].trim();
  if (regionPart.length === 2) return regionPart.toUpperCase();

  const mapped = REGION_NAME_TO_CODE[regionPart.toLowerCase()];
  if (mapped) return mapped;

  const words = regionPart
    .replace(/[^a-zA-Z\s]/g, " ")
    .split(/\s+/)
    .filter(Boolean)
    .filter(
      (w) =>
        ![
          "state",
          "province",
          "region",
          "city",
          "metropolitan",
          "county",
          "of",
          "the",
        ].includes(w.toLowerCase()),
    );

  if (words.length >= 2) {
    return `${words[0][0]}${words[1][0]}`.toUpperCase();
  }
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  return "";
};

const parsePredictionDescription = (description) => {
  const raw = String(description || "");
  const addressOnly = raw.includes(" - ") ? raw.split(" - ").slice(1).join(" - ") : raw;
  const parts = addressOnly
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  if (parts.length === 0) {
    return {
      street: "",
      city: "",
      region: "",
      country: "",
      zip: "",
    };
  }

  const street = parts[0] || "";
  const countryText = parts.length >= 2 ? parts[parts.length - 1] : "";
  const regionText = parts.length >= 3 ? parts[parts.length - 2] : "";

  return {
    street,
    city: street,
    region: toRegionCode(regionText),
    country: normalizeCountryToISO2(countryText) || "",
    zip: "",
  };
};
// Reusable address autocomplete component using Google Places REST APIs.
// NOTE: Server-side proxy is used to protect the API key and avoid CORS issues.
const AddressAutocomplete = ({
  value = "",
  onChange,
  onManualOpen,
  onManualClose,
  manualInitialName,
  onManualSave,
  disabled = false,
  dropdownZIndex = 100000,
}) => {
  // Helper: Convert value (string or object) to display string
  const valueToString = (val) => {
    if (!val) return "";
    if (typeof val === "string") return val;
    if (typeof val === "object") {
      // Convert object to comma-separated string for display
      // Apartment/Unit number (address2) is prepended to the street so the
      // display reads e.g. "22, Kadahapola, Narammala, KU, 60181, LK".
      const addressParts = [
        val.address2 || val.line2,
        val.street,
        val.city,
        val.region,
        val.zip,
        val.country,
      ].filter(Boolean);
      if (val.name) {
        return `${val.name} - ${addressParts.join(", ")}`;
      }
      return addressParts.join(", ");
    }
    return "";
  };

  const [input, setInput] = useState(valueToString(value));
  const [predictions, setPredictions] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const debounceRef = useRef(null);
  const wrapperRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);
  const [dropdownStyle, setDropdownStyle] = useState(null);
  const [pendingAddress, setPendingAddress] = useState(null);

  // Use backend API proxy for all Places requests. Do NOT use a browser API key.
  const API_BASE = BASE_URL; // Use BASE_URL from config which uses REACT_APP_BACKEND_URL

  // Debug: indicate we are using the backend proxy (only in development)
  try {
    // eslint-disable-next-line no-console
    if (process && process.env && process.env.NODE_ENV === "development") {
      console.log(
        "AddressAutocomplete: using backend Places proxy; browser API key suppressed",
      );
    }
  } catch (e) {
    // ignore
  }

  // Using server-side proxy for Places (no Maps JS needed)
  useEffect(() => {
    console.log("🔍 AddressAutocomplete value changed:", value);
    setInput(valueToString(value));
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      // If click is outside the input wrapper AND outside the portal dropdown, close.
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        if (
          dropdownRef.current &&
          dropdownRef.current.contains &&
          dropdownRef.current.contains(e.target)
        ) {
          return; // clicked inside dropdown (portal) — ignore
        }
        setShowDropdown(false);

        // Revert input if NOT in manual mode and text has changed but not saved
        if (!showManual) {
          const savedString = valueToString(value);
          if (input !== savedString) {
            setInput(savedString);
          }
        }
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [input, showManual, value]); // include dependencies to ensure latest state is captured





  const fetchPredictions = async (text) => {
    const minLen = 2;
    if (!text || text.length < minLen) {
      setPredictions([]);
      return;
    }

    setLoading(true);
    try {
      // Call backend proxy which forwards to Google (avoids CORS and hides key)
      try {
        const url = `${API_BASE}/api/places/autocomplete?input=${encodeURIComponent(text)}`;
        const res = await fetch(url, { credentials: "include" });
        if (!res.ok) {
          console.error(
            "Places autocomplete API error:",
            res.status,
            res.statusText,
          );
          setPredictions([]);
          return;
        }
        const data = await res.json();
        if (data && data.predictions) setPredictions(data.predictions);
        else setPredictions([]);
      } catch (e) {
        console.error("Places autocomplete fetch error:", e);
        setPredictions([]);
      }
    } catch (e) {
      setPredictions([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlaceDetails = async (placeId) => {
    if (!placeId) return null;
    try {
      const url = `${API_BASE}/api/places/details?place_id=${encodeURIComponent(placeId)}`;
      const res = await fetch(url, { credentials: "include" });
      if (!res.ok) return null;
      const data = await res.json();

      if (data?.result?.address_components) {
        const r = data.result;
        const getComp = (type) =>
          r.address_components.find((c) => c.types.includes(type));

        const streetNum = getComp("street_number")?.long_name || "";
        const route = getComp("route")?.long_name || "";

        // City fallbacks: locality > sublocality_level_1 > sublocality > postal_town
        const city =
          getComp("locality")?.long_name ||
          getComp("sublocality_level_1")?.long_name ||
          getComp("sublocality")?.long_name ||
          getComp("postal_town")?.long_name ||
          getComp("neighborhood")?.long_name ||
          "";

        // State fallbacks: admin_area_lvl_1 > admin_area_lvl_2
        const state =
          getComp("administrative_area_level_1")?.short_name ||
          getComp("administrative_area_level_1")?.long_name ||
          getComp("administrative_area_level_2")?.short_name ||
          getComp("administrative_area_level_2")?.long_name ||
          "";

        const country = getComp("country")?.short_name || "";
        const zip = getComp("postal_code")?.long_name || "";

        let street = `${streetNum} ${route}`.trim();

        // If it's a specific place (like a building or business) but street components are missing
        if (!street && r.types.includes("establishment")) {
          street = r.name;
        }

        return {
          street,
          city,
          region: state,
          country,
          zip,
          // Only use name if it's different from the street or it's a known branding
          name:
            r.types &&
            !r.types.includes("street_address") &&
            !r.types.includes("premise") &&
            r.name !== street
              ? r.name
              : "",
        };
      }
      return null;
    } catch (e) {
      console.error("Places details fetch error:", e);
      return null;
    }
  };

  const onInputChange = (txt) => {
    setInput(txt);
    // Don't call onChange here - only update when user selects/saves
    setShowDropdown(true);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchPredictions(txt), 350);
  };

  const updateDropdownPosition = () => {
    const inputEl = inputRef.current;
    if (!inputEl) {
      setDropdownStyle(null);
      return;
    }

    // Small delay to ensure DOM is ready
    requestAnimationFrame(() => {
      const rect = inputEl.getBoundingClientRect();
      const padding = 8;
      const maxBelow = window.innerHeight - rect.bottom - padding;
      const maxAbove = rect.top - padding;

      const isAbove = maxBelow < 120 && maxAbove > maxBelow;
      const preferredMax = Math.min(360, Math.max(120, maxBelow));

      const style = {
        left: rect.left,
        width: rect.width,
        maxHeight: isAbove ? Math.min(360, maxAbove) : preferredMax,
        position: "fixed",
        zIndex: dropdownZIndex,
      };

      if (isAbove) {
        // Anchor bottom of dropdown to top of input
        style.bottom = window.innerHeight - rect.top + 2;
      } else {
        // Anchor top of dropdown to bottom of input
        style.top = rect.bottom + 2;
      }

      setDropdownStyle(style);
    });
  };

  useEffect(() => {
    if (!showDropdown) {
      setDropdownStyle(null);
      return;
    }

    // Update position immediately and on changes
    updateDropdownPosition();

    const onResize = () => updateDropdownPosition();
    window.addEventListener("resize", onResize);
    window.addEventListener("scroll", onResize, true);

    return () => {
      window.removeEventListener("scroll", onResize, true);
    };
  }, [showDropdown]);

  const handleSelectPrediction = async (pred) => {
    setShowDropdown(false);
    setLoading(true);
    try {
      const complexAddress = await fetchPlaceDetails(pred.place_id);
      const parsed = parsePredictionDescription(pred.description || "");

      if (complexAddress) {
        // If we got a structured name from details, use it.
        if (!complexAddress.name) {
          const potentialName = pred.structured_formatting?.main_text || "";
          if (potentialName && potentialName !== complexAddress.street) {
            complexAddress.name = potentialName;
          }
        }

        const mergedAddress = {
          ...complexAddress,
          street: complexAddress.street || parsed.street,
          city: complexAddress.city || parsed.city || "",
          region: toRegionCode(complexAddress.region || parsed.region),
          country: normalizeCountryToISO2(complexAddress.country) || parsed.country,
          zip: complexAddress.zip || "",
        };

        setPendingAddress(mergedAddress);
        setInput(valueToString(mergedAddress));
        onChange && onChange(mergedAddress);
      } else {
        // Fallback to plain string if detail fetch failed
        const final = pred.description || "";
        const fallback = parsePredictionDescription(final);
        if (!fallback.street) fallback.street = final;
        setPendingAddress(fallback);
        setInput(valueToString(fallback));
        onChange && onChange(fallback);
      }
    } catch (err) {
      console.error("Error handling prediction selection:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleManualSave = (manualObj) => {
    // manualObj: { address: {street, city, region, zip, country}, name: "..." }
    setShowManual(false);
    setShowDropdown(false);
    setPendingAddress(null);

    // Create complete address object with name
    const addressWithName = {
      ...manualObj.address,
      name: manualObj.name,
    };

    // Update input display
    setInput(valueToString(addressWithName));

    // Pass complete object to onChange
    onChange && onChange(addressWithName);

    // Also call onManualSave with the complete data for backward compatibility
    if (onManualSave) {
      onManualSave(manualObj);
    }

    onManualClose && onManualClose();
  };

  const handleOpenManual = () => {
    if (!pendingAddress && typeof value === "object" && value) {
      const parsed = parsePredictionDescription(input || valueToString(value));
      const countryFromValue =
        value.country === null
          ? ""
          : normalizeCountryToISO2(value.country) ?? value.country;
      setPendingAddress({
        ...value,
        name: value.name ?? "",
        street: value.street === null ? "" : value.street ?? parsed.street ?? "",
        city: value.city === null ? "" : value.city ?? parsed.city ?? "",
        region: value.region === null ? "" : value.region ?? parsed.region ?? "",
        country: countryFromValue ?? parsed.country ?? "",
        zip: value.zip === null ? "" : value.zip ?? "",
      });
    }
    setShowManual(true);
  };

  const handleFocus = () => {
    if (showManual) return;

    // If we have an existing value, clear predictions so they don't show up
    // until the user starts typing.
    const savedString = valueToString(value);
    if (input === savedString && input.trim() !== "") {
      setPredictions([]);
      setShowDropdown(false);
      return;
    }
    setShowDropdown(true);
  };

  const handleInputClick = () => {
    const savedString = valueToString(value);
    if (
      !showManual &&
      typeof value === "object" &&
      value &&
      savedString &&
      input === savedString
    ) {
      const parsed = parsePredictionDescription(input || savedString);
      const countryFromValue =
        value.country === null
          ? ""
          : normalizeCountryToISO2(value.country) ?? value.country;
      setPendingAddress({
        ...value,
        name: value.name ?? "",
        street: value.street === null ? "" : value.street ?? parsed.street ?? "",
        city: value.city === null ? "" : value.city ?? parsed.city ?? "",
        region: value.region === null ? "" : value.region ?? parsed.region ?? "",
        country: countryFromValue ?? parsed.country ?? "",
        zip: value.zip === null ? "" : value.zip ?? "",
      });
      setShowDropdown(false);
      setShowManual(true);
    }
  };

  const handleClearAddress = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setInput("");
    setPredictions([]);
    setShowDropdown(false);
    setPendingAddress(null);
    setShowManual(false);
    onChange && onChange(null);
  };

  return (
    <div className="relative" ref={wrapperRef}>
      <input
        type="text"
        className="w-full border border-border rounded-lg px-3 py-2 pr-10 bg-white text-foreground"
        placeholder="Start typing an address"
        value={input}
        onChange={(e) => onInputChange(e.target.value)}
        ref={inputRef}
        onFocus={handleFocus}
        onClick={handleInputClick}
        disabled={disabled}
      />
      {!disabled && input && (
        <button
          type="button"
          onMouseDown={handleClearAddress}
          onClick={handleClearAddress}
          className="absolute right-2 top-1/2 z-10 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-lg leading-none px-1"
          aria-label="Clear address"
          title="Clear address"
        >
          ×
        </button>
      )}

      {showDropdown &&
        dropdownStyle &&
        ReactDOM.createPortal(
          <div
            ref={dropdownRef}
            className="bg-white border border-gray-300 rounded-lg shadow-lg z-[100000] overflow-auto"
            style={dropdownStyle}
            role="listbox"
          >
            {loading && (
              <div className="p-2 text-sm text-gray-500">Loading...</div>
            )}
            {!loading && (
              <div>
                {/* Show predictions if user is typing and we have them */}
                {predictions.length > 0 &&
                  predictions.map((p, idx) => (
                    <button
                      key={p.place_id || idx}
                      type="button"
                      className="block w-full text-left px-3 py-2 hover:bg-gray-100 text-sm border-b border-gray-100 last:border-b-0"
                      onClick={() => handleSelectPrediction(p)}
                    >
                      {p.description}
                    </button>
                  ))}

                {/* Always show manual option at the bottom */}
                <button
                  type="button"
                  className="block w-full text-left px-3 py-2 hover:bg-gray-100 text-sm text-blue-600 font-medium pt-1"
                  onClick={handleOpenManual}
                  disabled={disabled}
                >
                  {input
                    ? "✏️ Edit address manually"
                    : "➕ Enter address manually"}
                </button>
              </div>
            )}
          </div>,
          document.body,
        )}

      {showManual && (
        <ManualAddressPopup
          initial={pendingAddress ? "" : input}
          initialValue={
            pendingAddress || (typeof value === "object" ? value : null)
          }
          initialName={
            pendingAddress
              ? pendingAddress.name ?? ""
              : (typeof value === "object" && value?.name) || manualInitialName
          }
          onCancel={() => {
            setShowManual(false);
            setShowDropdown(false);
            setPendingAddress(null);

            // Revert input field to saved value if it was a transient search/selection
            const savedString = valueToString(value);
            if (input !== savedString) {
              setInput(savedString);
            }

            onManualClose && onManualClose();
          }}
          onSave={handleManualSave}
        />
      )}
    </div>
  );
};

const ManualAddressPopup = ({
  initial = "",
  initialValue = null,
  initialName = "",
  onCancel,
  onSave,
}) => {
  const [line1, setLine1] = useState("");
  const [line2, setLine2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [country, setCountry] = useState("");
  const [locName, setLocName] = useState(initialName || "");

  useEffect(() => {
    // Priority 1: If initialValue object is provided, use it directly
    if (initialValue && typeof initialValue === "object") {
      setLine1(initialValue.street || "");
      setLine2(initialValue.address2 || initialValue.line2 || "");
      setCity(initialValue.city || "");
      const countryCode = String(initialValue.country || "").toUpperCase();
      const regionRaw = initialValue.region || initialValue.state || "";
      setState(toRegionDisplayName(regionRaw, countryCode));
      setZip(initialValue.zip || initialValue.postal_code || "");
      setCountry(countryCode);
      // Name priority: object.name > initialName prop
      setLocName(initialValue.name || initialName || "");
      return;
    }

    // Priority 2: Parse string
    if (!initial) {
      setLine1("");
      setCity("");
      setState("");
      setZip("");
      setCountry("");
      setLocName(initialName || "");
      return;
    }

    // Parse logic
    let addressToParse = initial;
    let serializedName = "";

    // Check if string has format "name - address"
    if (initial.includes(" - ")) {
      const dashIndex = initial.indexOf(" - ");
      const potentialName = initial.substring(0, dashIndex).trim();
      const potentialAddress = initial.substring(dashIndex + 3).trim();

      if (potentialName && potentialAddress) {
        serializedName = potentialName;
        addressToParse = potentialAddress;
      }
    }

    // Set name: name from string > initialName prop
    setLocName(serializedName || initialName || "");

    // Is the remaining string just a country?
    if (
      COUNTRY_NAME_TO_CODE[addressToParse] ||
      Object.values(COUNTRY_NAME_TO_CODE).includes(addressToParse)
    ) {
      const code = COUNTRY_NAME_TO_CODE[addressToParse] || addressToParse;
      setCountry(COUNTRY_CODE_TO_NAME[code] || code);
      setLine1("");
      setCity("");
      setState("");
      setZip("");
      if (!serializedName) setLocName(addressToParse); // In case of just "Denmark"
      return;
    }

    const strictParts = addressToParse.split(",").map((p) => p.trim());

    if (strictParts.length >= 5) {
      const pCountry = strictParts[strictParts.length - 1];
      const pZip = strictParts[strictParts.length - 2];
      const pState = strictParts[strictParts.length - 3];
      const pCity = strictParts[strictParts.length - 4];
      const pStreet = strictParts.slice(0, strictParts.length - 4).join(", ");

      setLine1(pStreet);
      setCity(pCity);
      setState(toRegionDisplayName(pState, pCountry));
      setZip(pZip);
      setCountry(COUNTRY_CODE_TO_NAME[pCountry] || pCountry);
      return;
    }

    const parts = strictParts.filter(Boolean);
    // Handle specific common short formats
    if (parts.length === 4) {
      // Logic: Street, City, State, Country ??? Or Street, City, State, Zip?
      // Our standard order: Street, City, State, Zip, Country
      // If 4 parts, assume Zip or Country is missing.
      // Let's try to detect if last part is country.
      const last = parts[parts.length - 1];
      if (COUNTRY_NAME_TO_CODE[last] || last.length === 2) {
        setLine1(parts[0]);
        setCity(parts[1]);
        setState(parts[2]);
        setCountry(COUNTRY_CODE_TO_NAME[last] || last);
      } else {
        setLine1(parts[0]);
        setCity(parts[1]);
        setState(parts[2]);
        setZip(parts[3]);
      }
    } else if (parts.length === 3) {
      // Could be Street, City, State or City, State, Country
      const last = parts[2];
      if (COUNTRY_NAME_TO_CODE[last] || last.length === 2) {
        setCity(parts[0]);
        setState(parts[1]);
        setCountry(COUNTRY_CODE_TO_NAME[last] || last);
      } else {
        setLine1(parts[0]);
        setCity(parts[1]);
        setState(parts[2]);
      }
    } else if (parts.length === 2) {
      // City, Country or State, Country or Street, City
      const last = parts[1];
      if (COUNTRY_NAME_TO_CODE[last] || last.length === 2) {
        setCity(parts[0]);
        setCountry(COUNTRY_CODE_TO_NAME[last] || last);
      } else {
        setLine1(parts[0]);
        setCity(parts[1]);
      }
    } else {
      setLine1(addressToParse);
    }
  }, [initial, initialValue, initialName]);

  // const compose = () => {
  //   // Convert name to code for storage (DB expects 2-char code)
  //   const code = COUNTRY_NAME_TO_CODE[country] || country;
  //   const parts = [line1, city, state, code, zip].filter(Boolean);
  //   return parts.join(', ');
  // };

  const regionCode = toRegionCode(state);

  const handleSave = () => {
    // Validate all required fields
    if (!city.trim()) {
      showError("City is required");
      return;
    }
    if (!regionCode || regionCode.length !== 2) {
      showError("Province/State is required");
      console.log("Invalid state input:", regionCode);

      return;
    }
    if (!country.trim()) {
      showError("Country is required");
      return;
    }
    if (!zip.trim()) {
      showError("ZIP/Postal Code is required");
      return;
    }

  const countryCode = country;

    // Send structured object to backend
    onSave({
      address: {
        street: line1,
        address2: line2,
        city,
        region: regionCode, // ✅ FIXED
        zip,
        country: countryCode,
      },
      name: locName,
    });
  };

  const node = (
    <>
      {/* Blur background */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm"
        style={{ zIndex: 100050 }}
        onClick={onCancel}
      ></div>

      {/* Modal */}
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{ zIndex: 100051 }}
      >
        <div className="bg-white p-6 rounded-xl shadow-lg w-full max-w-md border border-gray-200">
          <h4 className="font-semibold text-lg mb-4">Enter Address Manually</h4>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location Name
              </label>
              <input
                value={locName}
                onChange={(e) => setLocName(e.target.value)}
                placeholder="Enter location name"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Street
              </label>
              <input
                value={line1}
                onChange={(e) => setLine1(e.target.value)}
                placeholder="Enter street address"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Apartment/Unit number
              </label>
              <input
                value={line2}
                onChange={(e) => setLine2(e.target.value)}
                placeholder="Apartment/Unit number"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  City <span className="text-red-500">*</span>
                </label>
                <input
                  value={city}
                  onChange={(e) => setCity(e.target.value)}
                  placeholder="Enter city"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Province/State <span className="text-red-500">*</span>
                </label>
                <GooglePlacesInput
                  value={state}
                  onChange={(val) => {
                    // Keep the human-readable state name in UI.
                    setState(String(val || "").split(",")[0].trim());

                    // Auto-fill country from region suggestion when available.
                    const matchedCountryIso2 = extractCountryISO2FromRegionValue(val);
                    if (matchedCountryIso2) {
                      setCountry(matchedCountryIso2);
                      return;
                    }

                    // If no country in value, infer US/CA by known region names/codes.
                    const regionToken = String(val || "").split(",")[0].trim().toLowerCase();
                    if (REGION_CODE_TO_NAME.US[REGION_NAME_TO_CODE[regionToken] || ""]) {
                      setCountry("US");
                      return;
                    }
                    if (REGION_CODE_TO_NAME.CA[REGION_NAME_TO_CODE[regionToken] || ""]) {
                      setCountry("CA");
                    }
                  }}
                  placeholder="Enter province/state"
                  types="administrative_area_level_1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  ZIP/Postal Code <span className="text-red-500">*</span>
                </label>
                <input
                  value={zip}
                  onChange={(e) => setZip(e.target.value)}
                  placeholder="Enter ZIP/postal code"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-red-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country <span className="text-red-500">*</span>
                </label>
<GooglePlacesInput
  value={COUNTRY_CODE_TO_NAME[country] || country}
  onChange={(val) => {
    const iso2 = normalizeCountryToISO2(val);

    if (!iso2) {
      console.log("Invalid country input from Google:", val);
      return;
    }

    setCountry(iso2); // 🔒 ALWAYS ISO-2
  }}
  placeholder="Enter country"
  types="country"
/>


              </div>
            </div>
          </div>
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200">
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-gray-700"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </>
  );

  if (typeof document !== "undefined") {
    return ReactDOM.createPortal(node, document.body);
  }
  return node;
};

export default AddressAutocomplete;
